<script>
import { h } from 'vue'
export default {
  name: 'BTab',
  inject: {
    getBvTabs: {
      default: () => () => ({})
    }
  },
  props: {
    tag: {
      type: String,
      default: 'div'
    },
    active: {
      type: Boolean,
      default: false
    },
    buttonId: {
      type: String,
      default: null
    },
    disabled: Boolean,
    lazy: Boolean,
    noBody: Boolean,
    title: {
      type: String,
      default: ''
    },
    // Sniffed by `<b-tabs>` and added to nav `li.nav-item`
    titleItemClass: {
      type: [String, Array, Object],
      default: null
    },
    titleLinkAttributes: {
      type: Object,
      default: () => ({})
    },
    // Sniffed by `<b-tabs>` and added to nav `a.nav-link`
    titleLinkClass: {
      type: [String, Array, Object],
      default: null
    },
    id: {
      type: String,
      default: null
    }
  },
  emits: ['active', 'update:active', 'click'],
  data() {
    return {
      localId_: null,
      localActive: this.active && !this.disabled,
      show: false
    }
  },
  computed: {
    safeId() {
      // Computed property that returns a dynamic function for creating the ID
      // Reacts to changes in both `.id` and `.localId_` and regenerates a new function
      const id = this.id || this.localId_

      // We return a function that accepts an optional suffix string
      // So this computed prop looks and works like a method
      // but benefits from Vue's computed prop caching
      const fn = suffix => {
        if (!id) {
          return null
        }
        suffix = String(suffix || '').replace(/\s+/g, '_')
        return suffix ? id + '_' + suffix : id
      }
      return fn
    },
    bvTabs() {
      const t = this.getBvTabs()
      return t
    },
    // For parent sniffing of child
    _isTab() {
      return true
    },
    tabClasses() {
      const { localActive: active, disabled } = this

      return [
        {
          active,
          disabled,
          'card-body': this.bvTabs.card && !this.noBody
        },
        // Apply <b-tabs> `activeTabClass` styles when this tab is active
        active ? this.bvTabs.activeTabClass : null
      ]
    },
    controlledBy() {
      return this.buttonId || this.localId_
    },
    computedNoFade() {
      return !(this.bvTabs.fade || false)
    },
    computedLazy() {
      return this.bvTabs.lazy || this.lazy
    }
  },
  watch: {
    active(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue) {
          // If activated post mount
          this.activate()
        } else {
          if (!this.deactivate()) {
            // Tab couldn't be deactivated, so we reset the synced active prop
            // Deactivation will fail if no other tabs to activate
            this.$emit('update:active', this.localActive)
          }
        }
      }
    },
    disabled(newValue, oldValue) {
      if (newValue !== oldValue) {
        const { firstTab } = this.bvTabs
        if (newValue && this.localActive && firstTab) {
          this.localActive = false
          firstTab()
        }
      }
    },
    localActive(newValue) {
      // Make `active` prop work with `.sync` modifier
      this.$emit('update:active', newValue)
    }
  },
  mounted() {
    this.localId_ = `mz-tab-${this.$uuid}`
    this.registerTab()
  },
  updated() {
    // Force the tab button content to update (since slots are not reactive)
    // Only done if we have a title slot, as the title prop is reactive
    const { updateButton } = this.bvTabs
    if (updateButton && this.$slots.title) {
      updateButton(this)
    }
  },
  beforeUnmount() {
    // Inform `<b-tabs>` of our departure
    this.unregisterTab()
  },
  methods: {
    registerTab() {
      // Inform `<b-tabs>` of our presence
      const { registerTab } = this.bvTabs
      if (registerTab) {
        registerTab(this)
      }
    },
    unregisterTab() {
      // Inform `<b-tabs>` of our departure
      const { unregisterTab } = this.bvTabs
      if (unregisterTab) {
        unregisterTab(this)
      }
    },
    activate() {
      // Not inside a `<b-tabs>` component or tab is disabled
      const { activateTab } = this.bvTabs
      return activateTab && !this.disabled ? activateTab(this) : false
    },
    deactivate() {
      // Not inside a `<b-tabs>` component or not active to begin with
      const { deactivateTab } = this.bvTabs
      return deactivateTab && this.localActive ? deactivateTab(this) : false
    }
  },
  render() {
    const defaultSlot = this?.$slots?.default && typeof this.$slots.default === 'function' ? this.$slots.default : () => {}
    const $content = h(
      this.tag,
      {
        class: ['tab-pane', this.tabClasses],
        style: { display: this.localActive ? '' : 'none' },
        role: 'tabpanel',
        id: this.localId_,
        'aria-hidden': this.localActive ? 'false' : 'true',
        'aria-labelledby': this.controlledBy || null,
        ref: 'panel'
      },
      // Render content lazily if requested
      [this.localActive || !this.computedLazy ? defaultSlot() : null]
    )

    return $content
  }
}
</script>
